import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { 
  Paintbrush, Construction, TreePine, Trash2, Trees, 
  Sparkles, Home as HomeIcon, Hammer, HomeIcon as RoofIcon, 
  Layers, Columns, ArrowRight, ShieldCheck, HelpCircle 
} from "lucide-react";

export default function Industries() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeIndustry, setActiveIndustry] = useState(null);

  const industriesList = [
    { name: "Painting", icon: Paintbrush, desc: "Lead generation for interior/exterior residential painting, cabinet painting, and wall staining projects." },
    { name: "Concrete", icon: Construction, desc: "Exclusive inquiries for residential driveways, patios, concrete staining, stamped concrete, and slab pours." },
    { name: "Tree Removal", icon: TreePine, desc: "High-intent inquiries for professional tree scaling, tree cutting, stump grinding, and lot clearing." },
    { name: "Junk Removal", icon: Trash2, desc: "Consumer inquiries for full-house cleanouts, appliance hauling, construction debris removal, and garage clearing." },
    { name: "Landscaping", icon: Trees, desc: "Targeted inquiries for landscape design, sod installation, regular maintenance contracts, and paver hardscaping." },
    { name: "Artificial Turf", icon: Sparkles, desc: "Leads for synthetic grass lawn installation, backyard putting greens, and playground turf installation." },
    { name: "Remodeling", icon: HomeIcon, desc: "Qualified inquiries for complete home additions, kitchen remodels, bathroom renovations, and basement finishing." },
    { name: "Deck Builders", icon: Hammer, desc: "High-value inquiries for wooden/composite deck construction, custom patios, pergola building, and deck repairs." },
    { name: "Roofing", icon: RoofIcon, desc: "Residential leads for asphalt shingle replacement, tile roofs, commercial/residential roofing repairs, and inspections." },
    { name: "Siding", icon: Layers, desc: "Leads for vinyl siding installation, fiber cement siding, cedar shake siding, repairs, and visual improvements." },
    { name: "Gutters", icon: Columns, desc: "Targeted homeowner leads for seamless aluminum gutter installation, gutter guards, custom leaf filters, and repairs." }
  ];

  useEffect(() => {
    const focusInd = searchParams.get("focus");
    if (focusInd) {
      const found = industriesList.find(i => i.name.toLowerCase() === focusInd.toLowerCase());
      if (found) {
        setActiveIndustry(found);
      }
    }
  }, [searchParams]);

  const handleSelectIndustry = (name) => {
    navigate(`/contact?industry=${encodeURIComponent(name)}`);
  };

  return (
    <div className="bg-white" data-testid="industries-page">
      
      {/* Page Header */}
      <section className="bg-slate-900 text-white py-16 md:py-24 text-center space-y-4">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 max-w-3xl space-y-3">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400">Target Verticals</span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
            Industries We Serve Across the U.S.
          </h1>
          <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
            Highly targeted lead acquisition systems custom engineered for 11 core residential trade sectors. Select your specialty to begin capturing exclusive homeowner leads.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          
          <div className="text-center max-w-2xl mx-auto space-y-3 mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Select Your Trade Specialty</h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              We separate each sector strictly so that you only receive inquiries for the exact home services you specialize in, leading to higher close rates.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industriesList.map((ind) => {
              const Icon = ind.icon;
              const isFocused = activeIndustry?.name === ind.name;
              return (
                <div 
                  key={ind.name}
                  onClick={() => setActiveIndustry(ind)}
                  className={`bg-white border rounded-lg p-8 shadow-sm transition-all cursor-pointer ${isFocused ? 'border-emerald-600 ring-2 ring-emerald-600/10 shadow-md' : 'border-slate-200 hover:border-slate-300 hover:shadow'}`}
                  data-testid={`industry-card-${ind.name.toLowerCase().replace(' ', '-')}`}
                >
                  <div className={`w-12 h-12 rounded flex items-center justify-center mb-6 border ${isFocused ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-slate-50 text-slate-900 border-slate-200'}`}>
                    <Icon className="h-6 w-6" />
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-2">{ind.name}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed mb-6">{ind.desc}</p>
                  
                  <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleSelectIndustry(ind.name); }}
                      className="inline-flex items-center text-xs font-semibold text-emerald-600 hover:text-emerald-700 hover:underline"
                      data-testid={`industry-request-leads-btn-${ind.name.toLowerCase().replace(' ', '-')}`}
                    >
                      Get Exclusive {ind.name} Leads
                      <ArrowRight className="ml-1 h-3.5 w-3.5" />
                    </button>
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">EXCLUSIVE</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Active Industry Detail Overlay banner */}
          {activeIndustry && (
            <div className="mt-16 bg-slate-50 border border-slate-200 rounded-lg p-8 animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="industry-focused-banner">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="space-y-2 max-w-2xl">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-emerald-600 animate-pulse" />
                    <span className="text-xs uppercase tracking-wider font-bold text-emerald-600">{activeIndustry.name} Campaigns Enabled</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 tracking-tight">
                    Qualified Inquiries Ready for Delivery in Your Area
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                    Our lead funnel has active {activeIndustry.name} consumer inquiries undergoing qualification right now. Let&apos;s secure exclusive coverage for your business zip codes.
                  </p>
                </div>
                <button 
                  onClick={() => handleSelectIndustry(activeIndustry.name)}
                  className="px-6 py-3 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors shadow shrink-0"
                  data-testid="industries-active-cta-btn"
                >
                  Secure {activeIndustry.name} Territory
                </button>
              </div>
            </div>
          )}

        </div>
      </section>

    </div>
  );
}
